import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Camera, Save } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'
import { Scene } from '../../types'

interface SceneSettingsDialogProps {
  open: boolean
  onClose: () => void
  scene: Scene | null
}

const SceneSettingsDialog: React.FC<SceneSettingsDialogProps> = ({ 
  open, 
  onClose, 
  scene 
}) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const { updateScene, loading } = useProjectStore()

  // Reset form when scene changes or dialog opens
  useEffect(() => {
    if (open && scene) {
      setTitle(scene.title)
      setDescription(scene.description || '')
    }
  }, [open, scene])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!scene || !title.trim()) return

    try {
      await updateScene(scene.id, {
        title: title.trim(),
        description: description.trim()
      })
      handleClose()
    } catch (error) {
      console.error('Failed to update scene:', error)
    }
  }

  const handleClose = () => {
    onClose()
  }

  if (!open || !scene) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-md border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <Camera className="w-5 h-5 text-primary-300" />
              </div>
              <h2 className="text-xl font-semibold text-white">Scene Settings</h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Scene Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter scene title..."
                className="glass-input w-full"
                maxLength={100}
                required
                autoFocus
              />
              <div className="text-xs text-white/50 mt-1">
                {title.length}/100 characters
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this scene (optional)..."
                className="glass-input w-full h-24 resize-none"
                maxLength={500}
              />
              <div className="text-xs text-white/50 mt-1">
                {description.length}/500 characters
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 glass-button text-white/90 hover:bg-white/10 py-2.5"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-semibold py-2.5 disabled:opacity-50 flex items-center justify-center space-x-2"
                disabled={loading || !title.trim()}
              >
                <Save className="w-4 h-4" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default SceneSettingsDialog

